import {
  DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING,
  ShopifyProductMatchMapping,
} from '@domain/connection/models/ConnectionSettings';

export interface ShopifySetupProductPreview {
  title: string;
  imageUrl?: string;
  variantTitle?: string;
  fieldValues: Record<string, string | null>;
}

export interface ShopifyMatchedJuststockProduct {
  id: string;
  title: string;
  image?: string;
  ean?: string;
  sku?: string;
}

/** Unified setup preview: Shopify catalog scan + Juststock match results. */
export interface ShopifyMatchPreviewResponse {
  mapping: ShopifyProductMatchMapping;
  fieldCounts: Record<string, number>;
  productsScanned: number;
  productsFound: number;
  products: ShopifySetupProductPreview[];
  hasDuplicateValues: boolean;
  duplicateValues: string[];
  matchesFound: number;
  matches: Record<string, ShopifyMatchedJuststockProduct>;
  nextPageToken?: string | null;
}

export interface CompleteShopifySetupResponse {
  jobs: { jobId: string; target: string }[];
}

export interface FinishShopifySetupInput {
  loadInventory: boolean;
}

export type ShopifyImportBannerTone = 'info' | 'warning' | 'error';

const TARGET_BY_SOURCE: Record<string, string> = {
  'variants.barcode': 'ean',
  'variants.sku': 'sku',
  'variants.id': 'customFields.shopifyVariantId',
};

export function suggestTargetForSource(sourceField: string, currentTarget?: string): string {
  return TARGET_BY_SOURCE[sourceField] ?? currentTarget ?? DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING.targetField;
}

export function formatPreviewValue(value: string | null | undefined): string {
  if (value == null || value === '') {
    return 'No value';
  }
  return value;
}

/** Values that appear on more than one scanned Shopify product for the field. */
export function getDuplicateSourceValues(
  products: ShopifySetupProductPreview[],
  sourceField: string,
): Set<string> {
  const counts = new Map<string, number>();

  for (const product of products) {
    const value = product.fieldValues[sourceField]?.trim();
    if (!value) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }

  const duplicates = new Set<string>();
  for (const [value, count] of counts.entries()) {
    if (count > 1) duplicates.add(value);
  }
  return duplicates;
}

export function getImportBannerTone(
  productsFound: number,
  productsScanned: number,
  hasDuplicateValues = false,
): ShopifyImportBannerTone {
  if (productsFound <= 0 || hasDuplicateValues) return 'error';
  if (productsFound < productsScanned) return 'warning';
  return 'info';
}
